import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import SecondaryMenu from '../../menu/secondaryMenu.component';
import PeriodDropdown from '../../shared/periodDropdown.component';
import { PeriodPropTypes } from '../../shared/propTypes/period';
import { OptionsPropType } from '../../shared/propTypes/common';
import FormikProvider from '../../shared/forms/formikProvider.component';
import Form from '../../shared/forms/form.component';
import { FormSchemaPropTypes, FormikPropTypes } from '../../shared/forms/propTypes';
import { periodOrCurrentSelector } from '../../shared/store/selectors';
import { fetchAccounts } from '../../shared/store/actions';

import useTaxEntriesTree from '../shared/useTaxEntriesTree';
import useBsmContext from '../shared/useBsmContext';
import { accountActivityPropType } from '../shared/propTypes';
import { accountsSelector } from '../store/selectors';

import { fetchAccountActivity, resetDataForAccountActivityPage } from './store/actions';
import {
  accountActivitySelector,
  accountActivityLoadingSelector,
} from './store/selectors';
import schema from './accountActivity.schema';
import AccountActivityGrid from './accountActivityGrid.component';

const renderForm = ({
  fetchAccountActivity,
  setChosenValuesText,
  period,
  accounts,
}) => {
  const AccountActivityMenu = ({ formik, schema }) => {
    const {
      companyCodes,
      taxYears,
      countryTaxTypes,
      entryTypes,
      entryActivities,
      bookTypes,
      combination,
    } = useTaxEntriesTree(formik, { forEntry: false, forView: true });

    useBsmContext(formik);

    const {
      companyCode,
      taxYear,
      countryTaxType,
      entryType,
      entryActivity,
      bookType,
      account,
    } = formik.values;

    const requiredDeps = [period.name, companyCode, taxYear, countryTaxType, entryType, entryActivity, bookType];

    useEffect(() => {
      if (combination.accountOverride) {
        formik.setFieldValue('account', combination.accountOverride);
      }
    }, [combination.accountOverride]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (!requiredDeps.every(Boolean)) {
        return;
      }

      if (!combination.accountOverride && combination.requireAccount && !account) {
        return;
      }

      fetchAccountActivity({
        period,
        companyCode,
        taxYear,
        countryTaxType,
        entryType,
        entryActivity,
        bookType,
        account,
      });

      setChosenValuesText(requiredDeps.join(' - '));
    }, [...requiredDeps, account]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <Form
        formik={formik}
        schema={schema}
        formContext={{
          companyCodes,
          taxYears,
          countryTaxTypes,
          entryTypes,
          entryActivities,
          bookTypes,
          combination,
          accounts,
        }}
      />
    );
  };

  AccountActivityMenu.propTypes = {
    formik: FormikPropTypes,
    schema: FormSchemaPropTypes,
  };

  return AccountActivityMenu;
};

const ViewAccountActivityPage = ({
  fetchAccountActivity,
  fetchAccounts,
  accountActivity,
  accountActivityLoading,
  accounts,
  period,
  resetDataForAccountActivityPage,
}) => {
  const [chosenValuesText, setChosenValuesText] = useState('');

  useEffect(() => {
    fetchAccounts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => resetDataForAccountActivityPage(), [resetDataForAccountActivityPage]);

  return (
    <div className="view-account-activity-page">
      <SecondaryMenu>
        <PeriodDropdown />
        <FormikProvider
          schema={schema}
          render={renderForm({
            fetchAccountActivity,
            setChosenValuesText,
            period,
            accounts,
          })}
        />
      </SecondaryMenu>

      <div className="page">
        <div className="container-fluid">
          <div className="row no-gutters page-content-container">
            <div className="col">
              <AccountActivityGrid
                accountActivity={accountActivity}
                chosenValuesText={chosenValuesText}
                accountActivityLoading={accountActivityLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ViewAccountActivityPage.propTypes = {
  fetchAccountActivity: PropTypes.func.isRequired,
  fetchAccounts: PropTypes.func.isRequired,
  accountActivityLoading: PropTypes.bool.isRequired,
  period: PeriodPropTypes.isRequired,
  accountActivity: PropTypes.arrayOf(accountActivityPropType),
  accounts: OptionsPropType,
  resetDataForAccountActivityPage: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  period: periodOrCurrentSelector(state),
  accounts: accountsSelector(state),
  accountActivity: accountActivitySelector(state),
  accountActivityLoading: accountActivityLoadingSelector(state),
});

export default connect(mapStateToProps, {
  fetchAccountActivity,
  fetchAccounts,
  resetDataForAccountActivityPage,
})(ViewAccountActivityPage);
